import { useTranslation } from 'react-i18next';
import RentTutorialCwalk from '../../components/RentTutorialCwalk';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function RentTutorial() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);

  const handleStart = async () => {
    // ボタンの状態を変更してローディング表示
    setIsNavigating(true);
    
    // 少し遅延を入れてアニメーションを見せる
    setTimeout(() => {
      navigate('/tennouzu/guide');
    }, 300);
  };

  return (
    <div className="rent-tutorial-container">
      <RentTutorialCwalk />
      
      <div className="button-section">
        <button 
          type="button" 
          className={`mode-button bottom-section ${isNavigating ? 'loading' : ''}`}
          onClick={handleStart}
          disabled={isNavigating}
          aria-label={t('Start guide tour')}
        >
          <span className="text-wrapper-6">
            {isNavigating ? t('Starting...') : t('Start')}
          </span>
        </button>
      </div>

      <style jsx>{`
        .rent-tutorial-container {
          display: flex;
          flex-direction: column;
          min-height: 100vh;
        }

        .button-section {
          margin-top: auto; /* コンテンツを上に、ボタンを下に押す */
          padding: 40px 20px;
          background: linear-gradient(to top, rgba(255, 255, 255, 0.95) 0%, transparent 100%);
          display: flex;
          justify-content: center;
        }

        /* 画面最下部配置のボタンスタイル */
        .mode-button.bottom-section {
          /* ベーススタイル */
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          max-width: 400px;
          padding: 18px 32px;
          
          /* フォント */
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: 0.5px;
          
          /* カラーとボーダー */
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          border-radius: 50px;
          
          /* シャドウ */
          box-shadow: 
            0 8px 32px rgba(102, 126, 234, 0.3),
            0 4px 16px rgba(0, 0, 0, 0.1);
          
          /* トランジション */
          transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          cursor: pointer;
          user-select: none;
          position: relative;
          overflow: hidden;
        }

        /* ホバーエフェクト（デスクトップのみ） */
        @media (hover: hover) {
          .mode-button.bottom-section:hover {
            transform: translateY(-2px) scale(1.02);
            box-shadow: 
              0 12px 40px rgba(102, 126, 234, 0.4),
              0 8px 24px rgba(0, 0, 0, 0.15);
            background: linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%);
          }
        }

        /* アクティブ状態 */
        .mode-button.bottom-section:active {
          transform: translateY(-1px) scale(1.01);
          box-shadow: 
            0 6px 20px rgba(102, 126, 234, 0.3),
            0 2px 8px rgba(0, 0, 0, 0.1);
        }

        /* フォーカス状態 */
        .mode-button.bottom-section:focus {
          outline: none;
          box-shadow: 
            0 8px 32px rgba(102, 126, 234, 0.3),
            0 4px 16px rgba(0, 0, 0, 0.1),
            0 0 0 4px rgba(102, 126, 234, 0.2);
        }

        /* ローディング状態 */
        .mode-button.bottom-section.loading {
          background: linear-gradient(135deg, #9ca3af 0%, #6b7280 100%);
          cursor: wait;
          animation: pulse 2s infinite;
          transform: none;
        }

        .mode-button.bottom-section.loading .text-wrapper-6::after {
          content: '⟳';
          animation: spin 1s linear infinite;
          margin-left: 8px;
        }

        /* テキストスタイル */
        .mode-button.bottom-section .text-wrapper-6 {
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        /* アイコン（矢印）付きバージョン */
        .mode-button.bottom-section:not(.loading) .text-wrapper-6::after {
          content: '→';
          font-size: 16px;
          margin-left: 8px;
          transition: transform 0.2s ease;
        }

        @media (hover: hover) {
          .mode-button.bottom-section:hover .text-wrapper-6::after {
            transform: translateX(4px);
          }
        }

        /* 背景のアニメーション効果 */
        .mode-button.bottom-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(
            90deg,
            transparent,
            rgba(255, 255, 255, 0.2),
            transparent
          );
          transition: left 0.6s ease;
        }

        @media (hover: hover) {
          .mode-button.bottom-section:hover::before {
            left: 100%;
          }
        }

        /* アニメーション */
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.8; }
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* ダークテーマ対応 */
        @media (prefers-color-scheme: dark) {
          .button-section {
            background: linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, transparent 100%);
          }
          
          .mode-button.bottom-section {
            background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
            box-shadow: 
              0 8px 32px rgba(79, 70, 229, 0.3),
              0 4px 16px rgba(0, 0, 0, 0.2);
          }
          
          @media (hover: hover) {
            .mode-button.bottom-section:hover {
              background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
              box-shadow: 
                0 12px 40px rgba(79, 70, 229, 0.4),
                0 8px 24px rgba(0, 0, 0, 0.25);
            }
          }
        }

        /* レスポンシブ対応 */
        @media (max-width: 768px) {
          .button-section {
            padding: 30px 20px;
          }
          
          .mode-button.bottom-section {
            font-size: 16px;
            padding: 16px 28px;
          }
        }

        /* 非常に小さい画面対応 */
        @media (max-width: 480px) {
          .button-section {
            padding: 20px 16px;
          }
          
          .mode-button.bottom-section {
            font-size: 15px;
            padding: 14px 24px;
          }
        }
      `}</style>
    </div>
  );
}